import apiService from '../services/apiService';

// Convenção de key do S3 combinada com o app mobile (mesmo prefixo que ele já usa
// em produção): chat/{stores.name técnico}/{conversation_id}/{kind}__{carimbo}.{ext}
// O carimbo aqui só precisa ser único (o mobile usa hora local do device, mas o
// handoff é explícito que qualquer sufixo serve) — usamos a hora local do browser
// no mesmo formato só por legibilidade/depuração.
const pad = (n) => String(n).padStart(2, '0');

const buildTimestamp = (date = new Date()) => {
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}_${pad(date.getHours())}-${pad(date.getMinutes())}-${pad(date.getSeconds())}`;
};

export const buildAttachmentKey = ({ storeName, conversationId, kind, ext }) => {
    return `chat/${storeName}/${conversationId}/${kind}__${buildTimestamp()}.${ext}`;
};

/**
 * Sobe o anexo pro nosso backend, que faz o PUT pro S3 por trás (o bucket não
 * tem CORS liberado pra PUT direto do navegador — isso nunca foi exercitado
 * antes porque o único outro uploader é o app mobile, que sobe nativo, fora
 * do alcance de CORS). Usa a `key` da RESPOSTA como referência real do
 * arquivo — o endpoint pode ter trocado espaços por underscore no fileName.
 */
export const uploadAttachmentToS3 = async ({ blob, fileName, bucketName, fileType }) => {
    const result = await apiService.uploadChatAttachment({ file: blob, fileName, bucketName, fileType });
    if (!result.success) {
        return { success: false, message: result.message || 'Erro ao subir anexo' };
    }
    return { success: true, key: result.key || fileName };
};

// ===== Gravação de áudio no navegador =====

const AUDIO_MIME_CANDIDATES = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];

export const pickRecorderMimeType = () => {
    if (typeof window === 'undefined' || !window.MediaRecorder) return '';
    return AUDIO_MIME_CANDIDATES.find(t => MediaRecorder.isTypeSupported(t)) || '';
};

// Safari já grava em audio/mp4 (AAC) nativamente — não precisa converter.
export const isAlreadyM4a = (mimeType) => !!mimeType && mimeType.includes('audio/mp4');

// ===== Conversão pra AAC/m4a via ffmpeg.wasm =====
// Carregado sob demanda (dynamic import) e só quando o admin realmente grava
// áudio em navegador que não grava nativamente em m4a — evita puxar o core do
// ffmpeg (~30MB de wasm, vindo do jsdelivr, já liberado no CSP) pra quem nunca
// usa a função.

let ffmpegInstancePromise = null;

const loadFfmpeg = async () => {
    if (!ffmpegInstancePromise) {
        ffmpegInstancePromise = (async () => {
            const { FFmpeg } = await import('@ffmpeg/ffmpeg');
            const { toBlobURL } = await import('@ffmpeg/util');
            const baseURL = 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/umd';
            const ffmpeg = new FFmpeg();
            await ffmpeg.load({
                coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
                wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm')
            });
            return ffmpeg;
        })();
    }
    return ffmpegInstancePromise;
};

/**
 * Converte um Blob de áudio gravado no navegador (webm/ogg, o que o
 * MediaRecorder produz no Chrome/Firefox) pra AAC dentro de um container m4a —
 * o único formato de áudio que o app mobile sabe tocar em bolha de chat hoje.
 */
export const transcodeToM4a = async (blob) => {
    const { fetchFile } = await import('@ffmpeg/util');
    const ffmpeg = await loadFfmpeg();

    const inputExt = blob.type.includes('ogg') ? 'ogg' : 'webm';
    const inputName = `input.${inputExt}`;
    const outputName = 'output.m4a';

    await ffmpeg.writeFile(inputName, await fetchFile(blob));
    await ffmpeg.exec(['-i', inputName, '-c:a', 'aac', '-b:a', '128k', outputName]);
    const data = await ffmpeg.readFile(outputName);

    try { await ffmpeg.deleteFile(inputName); } catch (_) { /* best-effort */ }
    try { await ffmpeg.deleteFile(outputName); } catch (_) { /* best-effort */ }

    return new Blob([data.buffer], { type: 'audio/mp4' });
};
